import type { ButtonHTMLAttributes } from 'react'

/** Button variants for the brand design language (Inter + blue/white):
 *  - primary / secondary: for WHITE surfaces (scan, manual entry, error cards)
 *  - accent / onBlue / ghost: for BLUE surfaces (customer + celebration screens)
 *    accent = medium-blue tile, onBlue = light "Fermer" CTA, ghost = translucent */
type Variant = 'primary' | 'secondary' | 'accent' | 'onBlue' | 'ghost'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
}

const styles: Record<Variant, string> = {
  primary: 'bg-brand text-white hover:bg-brand-dark disabled:opacity-50',
  secondary: 'bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50',
  accent: 'bg-brand-500 text-white hover:bg-brand-500/85 disabled:opacity-50',
  onBlue: 'bg-brand-300 text-white hover:bg-brand-300/85 disabled:opacity-60',
  ghost: 'bg-white/15 text-white hover:bg-white/25 disabled:opacity-50',
}

export function Button({ variant = 'primary', className = '', type, ...props }: ButtonProps) {
  return (
    <button
      // Default to "button" so a Button outside a form never submits by accident; callers that
      // need a submit (e.g. the manual-entry form) still pass type="submit".
      type={type ?? 'button'}
      {...props}
      className={`w-full rounded-xl px-4 py-3 text-base font-semibold transition disabled:cursor-not-allowed ${styles[variant]} ${className}`}
    />
  )
}
